import SearchIcon from '@mui/icons-material/Search';
import { Box, IconButton, Paper, Popper, TextField, Tooltip, Typography, Zoom, useMediaQuery } from '@mui/material';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import { styled, useTheme } from '@mui/material/styles';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import PropTypes from 'prop-types';
import React, { createContext, useContext, useEffect, useRef } from "react";
import { VariableSizeList } from 'react-window';

import * as send from '../../../../../../lib/analytics/send';
import * as gtag from "../../../../../../lib/google/gtag";
import { dataConstants } from "../../../../../constants";

const { HISTORICALCHART_OPTIONS } = dataConstants;

const LISTBOX_PADDING = 8; // px

function renderRow(props) {
  const { data, index, style } = props;
  const child = data[index];
  const inlineStyle = { ...style, top: style.top + LISTBOX_PADDING };

  if (!React.isValidElement(child)) {
    return null;
  }

  return React.cloneElement(child, {
    style: { ...child.props.style, ...inlineStyle }
  });
}
renderRow.propTypes = {
  data: PropTypes.array.isRequired,
  index: PropTypes.number.isRequired,
  style: PropTypes.object.isRequired,
};

const OuterElementContext = createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});
OuterElementType.displayName = "OuterElementType";

function useResetCache(data) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
  const { children, ...other } = props;

  const itemData = React.Children.toArray(children);

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });

  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getHeight = () => {
    return Math.min(8, itemCount) * itemSize;
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={() => itemSize}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});
ListboxComponent.propTypes = { children: PropTypes.node };

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': { padding: 0, margin: 0 },
  },
});

function sleep(delay = 0) {
  return new Promise((resolve) => setTimeout(resolve, delay));
}

const SearchHistoricalCharts = ({ setShowSearch, ticker, placeholder, setLoading, setSeries }) => {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState([]);

  const loading = open && options.length === 0;

  const handleSearch = (event, search_term) => {
    if (!search_term) return;

    const isString = typeof search_term === 'string';
    const shortName = isString ? search_term : (search_term.shortName || '');
    const aggCode = isString ? search_term : (search_term.aggCode || '');
    const legend = isString ? search_term : (search_term.legend || '');

    setLoading(true);
    setSeries({
      seriesCode: `XXXXX.${aggCode}`,
      seriesName: shortName,
      legend: legend,
      code: aggCode,
    });

    send.uevent(event, `historical-chart-search-${ticker || 'unknown'}.${aggCode}`);
    gtag.event({ action: "historical_chart_search", search_term: shortName });
  };

  React.useEffect(() => {
    let active = true;
    if (!loading) return undefined;

    const loadOptions = async () => {
      await sleep(500);
      if (active) {
        setOptions([...(HISTORICALCHART_OPTIONS || [])]);
      }
    };

    loadOptions();

    return () => { active = false; };
  }, [loading]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
      <Paper
        component="form"
        onSubmit={(e) => e.preventDefault()}
        sx={{
          borderRadius: '5px',
          border: "1px solid #dbdce4",
          display: 'flex',
          alignItems: 'center',
          margin: '0 auto',
          bgcolor: 'transparent',
          zIndex: open ? (theme) => theme.zIndex.appBar : 0,
          height: '2.4375em',
          boxShadow: 0,
        }}
      >
        <IconButton
          type="button"
          sx={{ p: '10px', position: 'absolute' }}
          aria-label="search"
          onClick={(e) => e.preventDefault()}
        >
          <SearchIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
        </IconButton>

        <Autocomplete
          autoHighlight
          id="search-fund"
          disableListWrap
          PopperComponent={StyledPopper}
          ListboxComponent={ListboxComponent}
          sx={{
            color: 'primary.main',
            transition: 'all ease-in-out .2s',
            fontSize: '.87rem',
            '& .MuiPaper-root-MuiAutocomplete-paper': { boxShadow: 5 },
            '& .MuiAutocomplete-input': { color: 'primary.main' },
            '&.MuiAutocomplete-root .MuiFilledInput-root .MuiAutocomplete-endAdornment svg': { color: 'primary.main' },
            '&.MuiAutocomplete-root .MuiFilledInput-root.MuiInputBase-sizeSmall .MuiFilledInput-input': {
              padding: '2.5px 0 2.5px 32px',
              fontSize: '.9rem'
            }
          }}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => { setOpen(false); setShowSearch(false); }}
          onChange={(event, newValue) => {
            if (newValue) handleSearch(event, newValue);
          }}
          isOptionEqualToValue={(option, val) => {
            const optStr = typeof option === 'string' ? option : option?.shortName;
            const valStr = typeof val === 'string' ? val : val?.shortName;
            return optStr === valStr;
          }}
          filterOptions={(opts, { inputValue }) => {
            if (!inputValue) return [];
            const lowerInput = inputValue.toLowerCase();
            return opts.filter(item =>
              (item?.shortName || '').toLowerCase().includes(lowerInput) ||
              (item?.aggCode || '').toLowerCase().includes(lowerInput)
            );
          }}
          options={options}
          loading={loading}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            if (option.inputValue) return option.inputValue;
            if (option.shortName) return option.shortName;
            if (option.aggCode) return option.aggCode;
            return '';
          }}
          selectOnFocus
          clearOnBlur
          clearOnEscape
          handleHomeEndKeys
          freeSolo
          renderOption={(props, option, { inputValue }) => {
            const { key, ...restProps } = props;
            const shortName = option?.shortName || '';
            const matches = match(shortName, inputValue || '');
            const parts = parse(shortName, matches);

            return (
              <li {...restProps} key={key}>
                <Tooltip title={shortName.length > 40 ? shortName : ''} disableInteractive TransitionComponent={Zoom}>
                  <Typography
                    component="div"
                    noWrap
                    sx={{
                      fontSize: '.75rem',
                      textTransform: 'uppercase',
                      color: 'text.description',
                      '&:hover': { color: 'text.color.white' },
                      width: '90%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', width: '98%' }}>
                      {parts.map((part, i) => (
                        <span
                          key={i}
                          style={{
                            fontWeight: part.highlight ? 700 : 400,
                            color: part.highlight ? 'hsl(235deg 59.62% 21.27%)' : 'inherit',
                          }}
                        >
                          {part.text}
                        </span>
                      ))}
                    </div>
                  </Typography>
                </Tooltip>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              size="small"
              variant="filled"
              hiddenLabel
              inputProps={{
                ...params.inputProps,
                'aria-label': placeholder || 'Search historical charts'
              }}
              sx={{
                width: { md: '49.4ch', xs: '59.4vw' },
                '& .MuiFilledInput-root': { py: '3px !important' },
                '& .MuiFilledInput-root, .MuiFilledInput-root:hover, .MuiFilledInput-root:focus': { background: 'transparent' },
                '& .MuiFilledInput-root:before, .MuiFilledInput-root:hover:before, .MuiFilledInput-root:after': { borderBottom: '0 !important' },
                '& .MuiInputBase-root-MuiFilledInput-root:before, .MuiInputBase-root-MuiFilledInput-root:hover:not(.Mui-disabled):before': { borderBottom: '0 !important' },
                '& .MuiInputBase-input-MuiFilledInput-input': { fontSize: '.87rem' },
              }}
            />
          )}
        />
      </Paper>
    </Box>
  );
};

SearchHistoricalCharts.propTypes = {
  setShowSearch: PropTypes.func.isRequired,
  ticker: PropTypes.string,
  placeholder: PropTypes.string,
  setLoading: PropTypes.func.isRequired,
  setSeries: PropTypes.func.isRequired,
};

export default React.memo(SearchHistoricalCharts);